import React from 'react';
import MarketingLayout from '../components/MarketingLayout';
import { Box, Container, Typography, Grid, Card, CardContent, Avatar, Rating, Chip } from '@mui/material';
import { testimonials, testimonialStats } from '../data/testimonialsContent';

const Testimonials = () => {
  return (
    <MarketingLayout>
    <Box sx={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #052d50 0%, #478289 100%)',
          color: 'white',
          py: 8,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            Investor Testimonials
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, maxWidth: 800, mx: 'auto' }}>
            Hear from our community of investors who are building their portfolios with Angels67
          </Typography>
        </Container>
      </Box>

      {/* Stats Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={4} sx={{ textAlign: 'center' }}>
          <Grid item xs={6} md={3}>
            <Typography variant="h3" sx={{ color: '#478289', fontWeight: 700 }}>
              {testimonialStats.averageRating}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Average Rating
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="h3" sx={{ color: '#478289', fontWeight: 700 }}>
              {testimonialStats.totalReviews}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Total Reviews
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="h3" sx={{ color: '#478289', fontWeight: 700 }}>
              {testimonialStats.satisfactionRate}%
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Satisfaction Rate
            </Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="h3" sx={{ color: '#478289', fontWeight: 700 }}>
              {testimonialStats.repeatInvestorRate}%
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Repeat Investors
            </Typography>
          </Grid>
        </Grid>
      </Container>

      {/* Testimonials Grid */}
      <Container maxWidth="lg" sx={{ py: 4, pb: 8 }}>
        <Grid container spacing={4}>
          {testimonials.map((testimonial) => (
            <Grid item xs={12} md={6} key={testimonial.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s',
                  '&:hover': { transform: 'translateY(-8px)', boxShadow: 6 },
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 4 }}>
                  {/* Header with Avatar and Info */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar
                      src={testimonial.avatar}
                      alt={testimonial.name}
                      sx={{ width: 80, height: 80, mr: 2 }}
                    />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {testimonial.name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                        {testimonial.title}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {testimonial.location}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Rating */}
                  <Rating value={testimonial.rating} readOnly sx={{ mb: 2 }} />

                  {/* Quote */}
                  <Typography
                    variant="body1"
                    sx={{
                      fontStyle: 'italic',
                      lineHeight: 1.8,
                      color: 'text.secondary',
                      mb: 3,
                      '&:before': { content: '"\u201C"', fontSize: '2rem', color: '#478289', mr: 0.5 },
                      '&:after': { content: '"\u201D"', fontSize: '2rem', color: '#478289', ml: 0.5 },
                    }}
                  >
                    {testimonial.quote}
                  </Typography>

                  {/* Footer Stats */}
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Chip
                      label={`${testimonial.investmentCount} Investments`}
                      size="small"
                      sx={{ backgroundColor: '#e3f2fd', fontWeight: 500 }}
                    />
                    <Chip
                      label={`Member since ${testimonial.memberSince}`}
                      size="small"
                      sx={{ backgroundColor: '#f1f8e9', fontWeight: 500 }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box sx={{ backgroundColor: 'white', py: 8 }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 600, color: '#052d50' }}>
            Join Our Community of Investors
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary', fontSize: '1.1rem' }}>
            Start building your portfolio with access to the best Israeli tech startups. Join thousands of investors
            who trust Angels67 for their venture capital investments.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Box
              component="a"
              href="/sso/signup"
              sx={{
                display: 'inline-block',
                px: 4,
                py: 2,
                backgroundColor: '#478289',
                color: 'white',
                borderRadius: 1,
                textDecoration: 'none',
                fontWeight: 600,
                fontSize: '1.1rem',
                '&:hover': { backgroundColor: '#3a6f71' },
              }}
            >
              Get Started
            </Box>
            <Box
              component="a"
              href="/how-it-works"
              sx={{
                display: 'inline-block',
                px: 4,
                py: 2,
                border: '2px solid #478289',
                color: '#478289',
                borderRadius: 1,
                textDecoration: 'none',
                fontWeight: 600,
                fontSize: '1.1rem',
                '&:hover': { backgroundColor: '#f5f5f5' },
              }}
            >
              Learn How It Works
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Trust Indicators */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <Typography variant="h4" sx={{ color: '#478289', mb: 1 }}>
                20,000+
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Active Investors
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <Typography variant="h4" sx={{ color: '#478289', mb: 1 }}>
                $400M+
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Assets Under Management
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <Typography variant="h4" sx={{ color: '#478289', mb: 1 }}>
                24
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Successful Exits
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  
    </MarketingLayout>
  );
};

export default Testimonials;
